import { BLUETIDE_DATASOURCES, getBluetideDataProxyBaseUrl } from './dataService';
import { extractPlatformSettingsFromSSEEvent, extractPlatformSettingsFromText, buildPlatformActionConfirmation } from '../utils/chatPlatformActions';
import {
  extractImagesFromContentPart,
  extractImagesFromPayload,
  extractImagesFromText,
  mergeUniqueImages,
  stripEmbeddedImagesFromText
} from '../utils/chatMedia';

/**
 * AI Service with External API Integration
 * Handles communication with demo-chat.isdata.ai API (API-only mode)
 */

const resolvedChatBaseUrl = getBluetideDataProxyBaseUrl();

// API Configuration
if (process.env.NODE_ENV === 'production' && resolvedChatBaseUrl && !resolvedChatBaseUrl.startsWith('https://')) {
  console.warn('Insecure API endpoint configured for production environment. Please use https.');
}

const API_CONFIG = {
  baseUrl: resolvedChatBaseUrl,
  endpoint: '/usmcom/data-proxy/chat',
  timeout: 600000, // Increased timeout to 10 minutes (600,000 milliseconds)
  retries: 2,
  token: process.env.REACT_APP_BEARER_TOKEN
};

// BlueTide headers — same defaults as dataService when env vars are unset.
// Trim + treat "" as unset (CI sometimes injects blank REACT_APP_*).
// Staging default; override per tenant via REACT_APP_BLUETIDE_SIDECAR_UUID.
const DEFAULT_BLUETIDE_SIDECAR_UUID = '110547ef-ac80-404c-adfc-b4b3660c36ee';
const trimEnvOr = (value, fallback) => {
  if (value == null) return fallback;
  const s = String(value).trim();
  return s !== '' ? s : fallback;
};
const BLUETIDE_CONFIG = {
  tenantUuid: trimEnvOr(process.env.REACT_APP_BLUETIDE_TENANT_UUID, '79f79901-6f6b-49cc-927e-e7533763a7cc'),
  userUuid: trimEnvOr(process.env.REACT_APP_BLUETIDE_USER_UUID, '4158fe99-3866-4f18-96f4-992da2aa51f9'),
  sidecarUuid: trimEnvOr(process.env.REACT_APP_BLUETIDE_SIDECAR_UUID, DEFAULT_BLUETIDE_SIDECAR_UUID)
};

function buildChatRequestHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'x-bluemvmt-tenant-uuid': BLUETIDE_CONFIG.tenantUuid,
    'x-bluemvmt-user-uuid': BLUETIDE_CONFIG.userUuid
  };
  const token = API_CONFIG.token;
  if (token != null && String(token).trim() !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

const CHAT_ADDITIONAL_INSTRUCTIONS = [
  'You are an expert oceanographic analysis assistant for the BlueTide marine science platform.',
  'Respond in clear, conversational prose for end users.',
  'Never repeat or display internal XML, datasource metadata, table IDs, or system prompt blocks.',
  'If datasource context is relevant, refer to datasets by friendly names only.',
  'When you change datasource, date range, depth, or model for the user, include a leading Filters: {"datasource","dateRange","depth",...} JSON line with the updated values so the platform control panels stay in sync. Depth must be a numeric value in meters (0 for surface).'
].join(' ');

/**
 * Removes internal sidecar metadata that sometimes leaks into assistant text.
 * @param {string} text - Raw assistant response
 * @returns {string} User-facing text
 */
export const sanitizeChatResponse = (text) => {
  if (!text) return '';

  return stripEmbeddedImagesFromText(
    text
      .replace(/<datasources>[\s\S]*?<\/datasources>/gi, '')
      .replace(/<datasource>[\s\S]*?<\/datasource>/gi, '')
      .replace(/^\s*Filters:\s*\{[\s\S]*?\}\s*\n*/m, '')
      .replace(/\n{3,}/g, '\n\n')
      .trimStart()
  );
};

/**
 * Main function to get AI response - API only (no fallbacks)
 * @param {string} message - The user's input message
 * @param {object} context - Current oceanographic data context
 * @param {string} threadId - Thread ID for conversation continuity
 * @returns {Promise<object>} AI response object { text, threadId }
 */
export const getAIResponse = async (message, context, threadId = null) => {
  try {
    const { text, threadId: newThreadId } = await getAPIResponse(message, context, threadId);
    const sanitized = sanitizeChatResponse(text);
    if (sanitized) {
      return { text: sanitized, threadId: newThreadId };
    }
    throw new Error('Empty response from API');
  } catch (error) {
    console.error('API request failed:', error.message);
    throw new Error(`AI service unavailable: ${error.message}`);
  }
};

/**
 * Streams AI response chunks as they arrive from the chat API.
 * @param {string} message - The user's input message
 * @param {object} context - Current oceanographic data context
 * @param {string} threadId - Thread ID for conversation continuity
 * @param {object} handlers - Stream callbacks
 * @param {(chunk: string) => void} handlers.onChunk - Sanitized text delta
 * @param {(threadId: string) => void} [handlers.onThreadId] - Thread ID when known
 * @param {(settings: object) => void} [handlers.onPlatformActions] - Control panel updates from chat
 * @returns {Promise<object>} Final response { text, threadId, streamedIncrementally }
 */
export const streamAIResponse = async (message, context, threadId = null, handlers = {}) => {
  const { onChunk, onThreadId, onPlatformActions, onImages } = handlers;
  let rawText = '';
  let lastSanitized = '';
  let streamedIncrementally = false;
  let platformActionsApplied = false;
  let collectedImages = [];

  const emitImages = (images) => {
    const merged = mergeUniqueImages(collectedImages, images);
    if (merged.length === collectedImages.length) return;
    collectedImages = merged;
    if (onImages) {
      onImages(collectedImages);
    }
  };

  const emitPlatformActions = (settings) => {
    if (!settings) return;
    platformActionsApplied = true;
    if (onPlatformActions) {
      onPlatformActions(settings);
    }
  };

  const emitSanitizedDelta = (chunk) => {
    if (!chunk) return;
    streamedIncrementally = true;
    rawText += chunk;
    emitImages(extractImagesFromText(chunk));
    const sanitized = sanitizeChatResponse(rawText);
    const delta = sanitized.slice(lastSanitized.length);
    lastSanitized = sanitized;
    if (delta && onChunk) {
      onChunk(delta);
    }
  };

  try {
    const result = await getAPIResponse(message, context, threadId, {
      onChunk: emitSanitizedDelta,
      onThreadId,
      onPlatformActions: emitPlatformActions,
      onImages: emitImages
    });

    let { text, threadId: newThreadId, images: resultImages } = result;
    if (!text && newThreadId) {
      const threadResult = await fetchThreadMessages(newThreadId);
      if (threadResult.text) {
        text = threadResult.text;
      }
      emitImages(threadResult.images);
    }

    emitImages(resultImages);

    const finalText = sanitizeChatResponse(text || rawText || '');
    if (!finalText) {
      const rawSettings = extractPlatformSettingsFromText(text || rawText || '');
      if (rawSettings) {
        emitPlatformActions(rawSettings);
      }
      if (platformActionsApplied) {
        return {
          text: buildPlatformActionConfirmation(rawSettings) || "I've updated the control panel filters.",
          threadId: newThreadId,
          streamedIncrementally: streamedIncrementally || Boolean(result.streamedIncrementally),
          images: collectedImages
        };
      }
      throw new Error('Empty response from API');
    }

    return {
      text: finalText,
      threadId: newThreadId,
      streamedIncrementally: streamedIncrementally || Boolean(result.streamedIncrementally),
      images: collectedImages
    };
  } catch (error) {
    console.error('API stream failed:', error.message);
    throw new Error(`AI service unavailable: ${error.message}`);
  }
};

/**
 * Makes request to external AI API and handles streaming SSE response
 * @param {string} message - User message
 * @param {object} context - Oceanographic context
 * @param {string} threadId - Thread ID for conversation continuity
 * @returns {Promise<string>} API response text
 */
const getAPIResponse = async (message, context, threadId = null, streamHandlers = null) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const payload = formatAPIPayload(message, context, threadId);
    const requestOptions = {
      method: 'POST',
      headers: buildChatRequestHeaders(),
      body: JSON.stringify(payload),
      signal: controller.signal
    };

    const response = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoint}`, requestOptions);

    clearTimeout(timeoutId);

    if (!response.ok) {
      let detail = '';
      try {
        detail = (await response.text()).trim();
      } catch (_) { /* ignore */ }
      const suffix = detail ? ` — ${detail.slice(0, 800)}` : '';
      throw new Error(`HTTP ${response.status}: ${response.statusText}${suffix}`);
    }

    // Handle streaming SSE response (text/event-stream)
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('text/event-stream') || contentType.includes('text/plain')) {
      if (response.body && streamHandlers) {
        const parsed = await consumeSSEStream(response.body, controller.signal, streamHandlers);
        if (!parsed.text && parsed.threadId) {
          const threadResult = await fetchThreadMessages(parsed.threadId, controller.signal);
          if (threadResult.text) {
            return {
              text: threadResult.text,
              threadId: parsed.threadId,
              images: mergeUniqueImages(parsed.images, threadResult.images)
            };
          }
        }
        return parsed;
      }

      let text = '';
      try {
        text = await response.text();
      } catch (streamError) {
        throw new Error('Chat stream closed before any response was received');
      }

      if (!text.trim()) {
        throw new Error('Chat API returned an empty stream (check sidecar UUID and staging chat service)');
      }

      const parsed = parseSSEResponse(text, streamHandlers);
      if (!parsed.text && parsed.threadId) {
        const threadResult = await fetchThreadMessages(parsed.threadId, controller.signal);
        if (threadResult.text) {
          return {
            text: threadResult.text,
            threadId: parsed.threadId,
            images: mergeUniqueImages(parsed.images, threadResult.images)
          };
        }
      }
      return parsed;
    }

    const data = await response.json();
    return extractResponseFromAPI(data);

  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('API request timed out');
    }
    throw error;
  }
};

const emitPlatformSettings = (settings, onPlatformActions) => {
  if (settings && onPlatformActions) {
    onPlatformActions(settings);
  }
};

const consumeSSEStream = async (body, signal, streamHandlers = {}) => {
  const { onChunk, onThreadId, onPlatformActions, onImages } = streamHandlers;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullResponse = '';
  let threadId = null;
  let streamedIncrementally = false;
  let images = [];

  const appendImages = (nextImages) => {
    const merged = mergeUniqueImages(images, nextImages);
    if (merged.length === images.length) return;
    images = merged;
    if (onImages) {
      onImages(images);
    }
  };

  const appendDelta = (chunk) => {
    if (typeof chunk !== 'string' || !chunk) return;
    fullResponse += chunk;
    streamedIncrementally = true;
    appendImages(extractImagesFromText(chunk));
    if (onChunk) {
      onChunk(chunk);
    }
  };

  const appendComplete = (chunk) => {
    if (typeof chunk !== 'string' || !chunk) return;
    fullResponse += chunk;
    appendImages(extractImagesFromText(chunk));
  };

  const processLine = (line) => {
    const trimmedLine = line.trim();
    if (!trimmedLine || trimmedLine.startsWith(':')) return;

    let jsonStr = null;
    if (trimmedLine.startsWith('data:')) {
      jsonStr = trimmedLine.slice(5).trimStart();
    } else if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) {
      jsonStr = trimmedLine;
    }
    if (!jsonStr || jsonStr === '[DONE]') return;

    try {
      const data = JSON.parse(jsonStr);
      if (data.thread_id) {
        threadId = data.thread_id;
        if (onThreadId) {
          onThreadId(data.thread_id);
        }
      }
      extractTextFromSSEEvent(data, {
        onDelta: appendDelta,
        onComplete: appendComplete,
        onPlatformActions,
        onImages: appendImages
      }, { preferIncremental: true });
    } catch (parseError) {
      console.warn('Failed to parse SSE line:', line, parseError);
    }
  };

  try {
    while (true) {
      if (signal?.aborted) {
        throw new Error('API request timed out');
      }

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        processLine(line);
      }
    }

    if (buffer.trim()) {
      processLine(buffer);
    }
  } finally {
    reader.releaseLock();
  }

  emitPlatformSettings(extractPlatformSettingsFromText(fullResponse), onPlatformActions);
  appendImages(extractImagesFromText(fullResponse));

  if (!fullResponse) {
    if (threadId) {
      return { text: '', threadId, streamedIncrementally: false, images };
    }
    throw new Error('Chat stream closed before any response was received');
  }

  return { text: fullResponse, threadId, streamedIncrementally, images };
};

const appendContentParts = (content, appendText, onImages) => {
  if (content == null) return;
  if (typeof content === 'string') {
    appendText(content);
    if (onImages) {
      onImages(extractImagesFromText(content));
    }
    return;
  }
  if (!Array.isArray(content)) return;
  for (const part of content) {
    if (!part) continue;
    if (onImages) {
      onImages(extractImagesFromContentPart(part));
    }
    if (part.type === 'output_text' && part.text) {
      appendText(part.text);
    } else if (part.type === 'text' && part.text) {
      appendText(typeof part.text === 'string' ? part.text : part.text.value || '');
    } else if (typeof part.text === 'string') {
      appendText(part.text);
    }
  }
};

const INTERNAL_SSE_TYPES = new Set([
  'thread_init',
  'function_call',
  'function_call_output',
  'tool_call',
  'tool_result'
]);

const isAssistantMessageEvent = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (data.type === 'assistant_message') return true;
  return data.type === 'message' && (data.role === 'assistant' || data.role === 'ai');
};

const extractIncrementalSSEEvent = (data, appendText) => {
  if (!data || typeof data !== 'object') return false;

  let extracted = false;
  const appendDelta = (chunk) => {
    if (typeof chunk === 'string' && chunk) {
      appendText(chunk);
      extracted = true;
    }
  };

  if (typeof data.delta === 'string') appendDelta(data.delta);
  if (typeof data.text_delta === 'string') appendDelta(data.text_delta);
  if (data.text_delta?.text) appendDelta(data.text_delta.text);
  if (data.type === 'delta' && data.text) appendDelta(data.text);

  if (data.type === 'message.delta' || data.type === 'content.delta') {
    if (typeof data.text === 'string') appendDelta(data.text);
    if (data.delta?.text) appendDelta(data.delta.text);
  }

  return extracted;
};

const extractTextFromSSEEvent = (data, sinks, { preferIncremental = false } = {}) => {
  const onDelta = sinks?.onDelta || sinks;
  const onComplete = sinks?.onComplete || sinks;
  const onPlatformActions = sinks?.onPlatformActions;
  const onImages = sinks?.onImages;

  const appendImages = (payload) => {
    if (!onImages) return;
    onImages(extractImagesFromPayload(payload));
  };

  if (!data || typeof data !== 'object') return;
  if (INTERNAL_SSE_TYPES.has(data.type)) {
    if (data.type === 'function_call_output' || data.type === 'tool_result') {
      emitPlatformSettings(extractPlatformSettingsFromSSEEvent(data), onPlatformActions);
      appendImages(data);
    }
    return;
  }

  appendImages(data);

  if (extractIncrementalSSEEvent(data, onDelta)) {
    return;
  }

  if (isAssistantMessageEvent(data)) {
    let messageText = '';
    appendContentParts(data.content, (chunk) => {
      messageText += chunk;
    }, onImages);
    emitPlatformSettings(extractPlatformSettingsFromText(messageText), onPlatformActions);
    appendContentParts(data.content, onComplete, onImages);
    return;
  }

  if (preferIncremental) {
    return;
  }

  if (typeof data.text === 'string') onComplete(data.text);
  if (typeof data.message === 'string') onComplete(data.message);
  if (typeof data.response === 'string') onComplete(data.response);
  if (data.type === 'message') {
    appendContentParts(data.content, onComplete, onImages);
  }

  if (data.type === 'delta' && data.text) {
    onDelta(data.text);
  }

  if (data.run_items && Array.isArray(data.run_items)) {
    onComplete(extractResponseFromAPI(data).text);
    appendImages(data.run_items);
  }

  if (data.item) {
    extractTextFromSSEEvent(data.item, sinks, { preferIncremental });
    appendContentParts(data.item.content, onComplete, onImages);
  }
};

/**
 * Parses Server-Sent Events (SSE) response text
 * @param {string} sseText - Raw SSE response text with 'data:' prefixed lines
 * @returns {object} Object containing message content and thread ID
 */
const parseSSEResponse = (sseText, streamHandlers = null) => {
  const onPlatformActions = streamHandlers?.onPlatformActions || streamHandlers;
  const lines = sseText.split(/\r?\n/);
  let fullResponse = '';
  let threadId = null;
  let images = [];

  const appendImages = (nextImages) => {
    images = mergeUniqueImages(images, nextImages);
    if (streamHandlers?.onImages) {
      streamHandlers.onImages(images);
    }
  };

  const appendText = (chunk) => {
    if (typeof chunk === 'string' && chunk) {
      fullResponse += chunk;
      appendImages(extractImagesFromText(chunk));
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine || trimmedLine.startsWith(':')) continue;

    let jsonStr = null;
    if (trimmedLine.startsWith('data:')) {
      jsonStr = trimmedLine.slice(5).trimStart();
    } else if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) {
      jsonStr = trimmedLine;
    }
    if (!jsonStr || jsonStr === '[DONE]') continue;

    try {
      const data = JSON.parse(jsonStr);
      if (data.thread_id) {
        threadId = data.thread_id;
      }
      extractTextFromSSEEvent(data, {
        onDelta: appendText,
        onComplete: appendText,
        onPlatformActions,
        onImages: appendImages
      }, { preferIncremental: true });
    } catch (parseError) {
      console.warn('Failed to parse SSE line:', line, parseError);
    }
  }

  emitPlatformSettings(extractPlatformSettingsFromText(fullResponse), onPlatformActions);
  appendImages(extractImagesFromText(fullResponse));

  if (!fullResponse) {
    const trimmed = sseText.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = extractResponseFromAPI(JSON.parse(trimmed));
        appendImages(extractImagesFromText(parsed.text));
        return { ...parsed, images };
      } catch (_) { /* not plain JSON */ }
    }

    if (threadId) {
      return { text: '', threadId, images };
    }

    if (process.env.NODE_ENV === 'development') {
      console.warn('Chat SSE parse failed. Raw preview:', sseText.slice(0, 1000));
    }
    throw new Error(`No message content found in streaming response (${sseText.length} bytes)`);
  }

  return { text: fullResponse, threadId, images };
};

const fetchThreadMessages = async (threadId, signal) => {
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}/messages/${encodeURIComponent(threadId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: buildChatRequestHeaders(),
    signal
  });

  if (!response.ok) {
    return { text: '', images: [] };
  }

  const data = await response.json();
  let text = '';
  let images = [];

  if (Array.isArray(data.messages)) {
    const assistantMessages = data.messages.filter(
      (message) => message?.role === 'assistant' || message?.role === 'ai'
    );
    const latest = assistantMessages[assistantMessages.length - 1];
    if (latest?.content) {
      if (typeof latest.content === 'string') {
        text = latest.content;
        images = extractImagesFromText(text);
      } else if (Array.isArray(latest.content)) {
        appendContentParts(latest.content, (chunk) => { text += chunk; }, (nextImages) => {
          images = mergeUniqueImages(images, nextImages);
        });
      }
      images = mergeUniqueImages(images, extractImagesFromPayload(latest));
    }
  }

  if (!text) {
    text = extractResponseFromAPI(data).text || '';
    images = mergeUniqueImages(images, extractImagesFromText(text), extractImagesFromPayload(data));
  }

  return { text, images };
};

/**
 * Formats payload for the BlueTide Chat API
 * @param {string} message - User message
 * @param {object} context - Oceanographic context
 * @param {string} threadId - Thread ID for conversation continuity
 * @returns {object} Formatted API payload
 */
const formatAPIPayload = (message, context, threadId = null) => {
  const {
    selectedModel = null,  // BlueTide datasources don't use models
    selectedArea = 'surface_vessel_data',  // Default BlueTide datasource
    selectedDepth = null,
    startDate,
    endDate
  } = context || {};

  // Build context object for prefixing
  const contextObj = {};
  if (selectedArea) contextObj.datasource = selectedArea;
  if (selectedDepth !== undefined && selectedDepth !== null) {
    contextObj.depth = selectedDepth;
  }
  if (selectedModel) contextObj.model = selectedModel;
  if (startDate) contextObj.dateRange = `${startDate} to ${endDate || startDate}`;

  // Prefix inputs with filters as JSON string
  const filterPrefix = Object.keys(contextObj).length > 0
    ? `Filters: ${JSON.stringify(contextObj)}\n\n`
    : '';

  const datasourceUuid = BLUETIDE_DATASOURCES[selectedArea];

  return {
    input: `${filterPrefix}${message}`,
    llm_model: 'blueai',
    datasource_uuids: datasourceUuid ? [datasourceUuid] : null,
    sidecar_uuid: BLUETIDE_CONFIG.sidecarUuid,
    thread_id: threadId || null,
    additional_instructions: CHAT_ADDITIONAL_INSTRUCTIONS
  };
};

/**
 * Extracts the response text from API response
 * @param {object} apiData - Raw API response
 * @returns {object} Object containing text and threadId (if available)
 */
const extractResponseFromAPI = (apiData) => {
  // Handle the documented API response structure
  let text = '';
  // Check common response formats
  if (apiData.run_items && Array.isArray(apiData.run_items)) {
    for (const item of apiData.run_items) {
      if (item.content && Array.isArray(item.content)) {
        for (const content of item.content) {
          if (content.type === 'output_text' && content.text) {
            text = content.text;
            break;
          }
        }
      }
    }
  } else if (apiData.response) {
    text = apiData.response;
  } else if (apiData.message) {
    text = apiData.message;
  } else if (apiData.content) {
    text = apiData.content;
  } else if (apiData.text) {
    text = apiData.text;
  } else if (apiData.output) {
    text = apiData.output;
  } else if (apiData.result) {
    text = apiData.result;
  } else if (typeof apiData === 'string') {
    text = apiData;
  } else {
    throw new Error('Invalid API response format');
  }

  return {
    text,
    threadId: apiData.thread_id || null
  };
};



/**
 * Lightweight chat API reachability check (no LLM call).
 * GET /chat/messages/{thread_id} — 404 means the service is up but the thread is missing.
 * @returns {Promise<boolean>}
 */
export const verifyChatConnection = async () => {
  if (!API_CONFIG.baseUrl) return false;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}/messages/__healthcheck__`;
    const response = await fetch(url, {
      method: 'GET',
      headers: buildChatRequestHeaders(),
      signal: controller.signal
    });
    return response.status < 500;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.warn('Chat API connection check failed:', error.message);
    }
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Get API status for monitoring
 * @returns {object} API status information
 */
export const getAPIStatus = () => {
  return {
    endpoint: `${API_CONFIG.baseUrl}${API_CONFIG.endpoint}`,
    timestamp: new Date().toISOString(),
    hasApiKey: !!API_CONFIG.token
  };
};

/**
 * Probe chat API and return status for the chatbot UI
 * @returns {Promise<object>} API status information including connected flag
 */
export const checkChatAPIStatus = async () => {
  const connected = await verifyChatConnection();
  return {
    ...getAPIStatus(),
    connected
  };
};

const aiService = {
  getAIResponse,
  streamAIResponse,
  sanitizeChatResponse,
  getAPIStatus,
  verifyChatConnection,
  checkChatAPIStatus
};

export default aiService;